import { Controller } from "@hotwired/stimulus";

/**
 * Toast Component Controller
 * 
 * Manages the lifecycle of individual toast notifications including:
 * - Auto-dismiss countdown with progress bar animation
 * - Manual dismiss
 * - Pause/resume on hover
 * - Memory-safe cleanup
 */
export default class extends Controller {
  static targets = ["toast", "progressBar"];
  static values = {
    duration: { type: Number, default: 5000 },
    autoDismiss: { type: Boolean, default: true }
  };

  declare readonly toastTarget: HTMLElement;
  declare readonly progressBarTarget?: HTMLElement;
  declare readonly hasProgressBarTarget: boolean;
  declare readonly durationValue: number;
  declare readonly autoDismissValue: boolean;

  private timeoutId: number | null = null;
  private intervalId: number | null = null;
  private startTime: number = 0;
  private remainingTime: number = 0;
  private isPaused: boolean = false;

  /**
   * Initialize toast on connect
   */
  connect(): void {
    this.remainingTime = this.durationValue;

    // Trigger entrance animation
    requestAnimationFrame(() => {
      this.toastTarget.classList.remove("toast--entering");
      this.toastTarget.classList.add("toast--visible");
    });

    if (this.autoDismissValue && this.durationValue > 0) {
      this.startCountdown();
    }

    // Add hover listeners for pause/resume
    this.toastTarget.addEventListener("mouseenter", this.pause.bind(this));
    this.toastTarget.addEventListener("mouseleave", this.resume.bind(this));
    
    // Add keyboard listener for Escape key
    this.toastTarget.addEventListener("keydown", this.handleKeyDown.bind(this));
  }

  /**
   * Clean up all timers and listeners on disconnect
   * CRITICAL: Prevents memory leaks
   */
  disconnect(): void {
    this.clearTimers();
    this.toastTarget.removeEventListener("mouseenter", this.pause.bind(this));
    this.toastTarget.removeEventListener("mouseleave", this.resume.bind(this));
    this.toastTarget.removeEventListener("keydown", this.handleKeyDown.bind(this));
  }

  /**
   * Handle keyboard events (Escape key to dismiss)
   */
  private handleKeyDown(event: KeyboardEvent): void {
    if (event.key === "Escape" || event.key === "Esc") {
      event.preventDefault();
      this.dismiss();
    }
  }

  /**
   * Dismiss the toast with exit animation
   */
  dismiss(): void {
    this.clearTimers();
    this.animateExit();
  }

  /**
   * Pause the countdown (triggered on hover)
   */
  pause(): void {
    if (this.isPaused || !this.autoDismissValue) return;

    this.isPaused = true;
    this.clearTimers();

    // Calculate remaining time
    const elapsed = Date.now() - this.startTime;
    this.remainingTime = Math.max(0, this.remainingTime - elapsed);

    // Pause progress bar animation
    if (this.hasProgressBarTarget) {
      const currentWidth = this.progressBarTarget!.getBoundingClientRect().width;
      const parentWidth = this.progressBarTarget!.parentElement!.getBoundingClientRect().width;
      const percentage = (currentWidth / parentWidth) * 100;
      this.progressBarTarget!.style.width = `${percentage}%`;
      this.progressBarTarget!.style.transition = "none";
    }
  }

  /**
   * Resume the countdown (triggered on mouse leave)
   */
  resume(): void {
    if (!this.isPaused || !this.autoDismissValue) return;

    this.isPaused = false;

    if (this.remainingTime > 0) {
      this.startCountdown();
    } else {
      this.dismiss();
    }
  }

  /**
   * Start or restart the countdown timer
   */
  private startCountdown(): void {
    this.startTime = Date.now();

    // Animate progress bar if present
    if (this.hasProgressBarTarget) {
      this.animateProgressBar();
    }

    // Set timeout for auto-dismiss
    this.timeoutId = window.setTimeout(() => {
      this.dismiss();
    }, this.remainingTime);
  }

  /**
   * Animate the progress bar countdown
   */
  private animateProgressBar(): void {
    if (!this.hasProgressBarTarget) return;

    const progressBar = this.progressBarTarget!;
    progressBar.style.transition = `width ${this.remainingTime}ms linear`;
    progressBar.style.width = "0%";

    // Update aria-valuenow for accessibility
    const updateInterval = 100; // Update every 100ms
    this.intervalId = window.setInterval(() => {
      const elapsed = Date.now() - this.startTime;
      const percentage = Math.max(0, 100 - (elapsed / this.durationValue) * 100);
      progressBar.setAttribute("aria-valuenow", Math.round(percentage).toString());

      if (percentage <= 0) {
        this.clearInterval();
      }
    }, updateInterval);
  }

  /**
   * Animate toast exit and remove from DOM
   */
  private animateExit(): void {
    this.toastTarget.classList.remove("toast--visible");
    this.toastTarget.classList.add("toast--exiting");

    // Remove from DOM after animation completes
    setTimeout(() => {
      this.toastTarget.remove();
    }, 200); // Match animation duration in CSS
  }

  /**
   * Clear all timers (timeout and interval)
   */
  private clearTimers(): void {
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    this.clearInterval();
  }

  /**
   * Clear the interval timer
   */
  private clearInterval(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
}
